/**
 * features/setup/ParentOnboarding.tsx
 * ------------------------------------------------------------------
 * Staff console for parent onboarding. Admin-gated
 * (canManageParentSettings), lives in the Setup section.
 *
 * Three workflow-ordered sub-tabs (in-page switcher):
 *   1. Registration Keys  — email the reconciled send-list per class
 *                           (review → approve → Send). The primary path.
 *   2. Parent Registrations — review pending public sign-ups → approve
 *                           (createParentAccount) / reject.
 *   3. Letters            — per-class letter PDF fallback, for families
 *                           without an email.
 *
 * Pure UI: all Firestore/CF calls are unchanged. No new write paths.
 * ------------------------------------------------------------------
 */
import { useMemo, useState, useEffect } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  UserRound,
  Printer,
  FileText,
  UserCheck,
  UserX,
  Copy,
  Inbox,
  Mail,
  Send,
  Ban,
  Check,
  UserCog,
  BarChart3,
  MailCheck,
  MailX,
  KeyRound,
  UserPlus,
  MessageSquare,
  Link2,
  RotateCcw,
} from 'lucide-react';
import { doc, collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, addDoc, updateDoc } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/ui/primitives';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildParentLetters, type ParentLetter } from '@/lib/parentLetterPdf';
import SchoolCommunications from '@/features/communications/SchoolCommunications';
import ChildLinks from '@/features/setup/ChildLinks';
import ParentLinkRequests from '@/features/setup/ParentLinkRequests';
import { PARENT_LINK_REQUESTS } from '@/lib/parentLinkRequests';
import { onSnapshot } from 'firebase/firestore';
import type { Guardian } from '@/types';

type SubTab = 'keys' | 'registrations' | 'requests' | 'messages' | 'links' | 'progress' | 'updates' | 'letters';
const GU_LABEL: Record<string, string> = { phone: 'Phone', whatsapp: 'WhatsApp', email: 'Email' };

export default function ParentOnboarding() {
  const { students, classes, loading } = useData();
  const { userData } = useAuth();

  const [tab, setTab] = useState<SubTab>('keys');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // ---- Parent Portal letters (per-class key letters) ----
  const [letterClassId, setLetterClassId] = useState('');
  const [letterBusy, setLetterBusy] = useState(false);
  const letterClasses = useMemo(
    () => classes,
    [classes],
  );

  async function generateLetters() {
    if (!letterClassId) { setMsg({ ok: false, text: 'Pick a class first.' }); return; }
    const cls = classes.find((c) => c.id === letterClassId);
    const classStudents = students.filter((s) => s.currentClass === letterClassId);
    if (classStudents.length === 0) { setMsg({ ok: false, text: 'No students in that class.' }); return; }
    setLetterBusy(true);
    setMsg(null);
    try {
      const ids = classStudents.map((s) => s.id);

      // Read existing keys for this class's students (chunked — `in` caps at 30).
      const readKeys = async () => {
        const byStudent = new Map<string, { key: string; status: string }[]>();
        for (let i = 0; i < ids.length; i += 30) {
          const chunk = ids.slice(i, i + 30);
          const snap = await getDocs(
            query(collection(db, 'parentAccessKeys'), where('studentId', 'in', chunk)),
          );
          snap.forEach((d) => {
            const k = d.data() as { studentId: string; key: string; status: string };
            const arr = byStudent.get(k.studentId) ?? [];
            arr.push({ key: k.key, status: k.status });
            byStudent.set(k.studentId, arr);
          });
        }
        return byStudent;
      };

      let keys = await readKeys();
      const hasStatus = (sid: string, st: string) =>
        (keys.get(sid) ?? []).some((k) => k.status === st);

      // Fresh keys only for students with NO unused AND NO redeemed key — so
      // already-registered children are never re-issued a key (or a letter).
      const needKeys = ids.filter((sid) => !hasStatus(sid, 'unused') && !hasStatus(sid, 'redeemed'));
      let created = 0;
      if (needKeys.length > 0) {
        const gen = httpsCallable<{ studentIds: string[] }, { generated: number; skipped: number }>(
          functions, 'generateParentKeys',
        );
        const res = await gen({ studentIds: needKeys });
        created = res.data?.generated ?? 0;
        keys = await readKeys(); // re-read to pick up the newly created keys
      }

      const alreadyDone = ids.filter((sid) => hasStatus(sid, 'redeemed') && !hasStatus(sid, 'unused')).length;

      // One letter per student holding an UNUSED key (redeemed excluded).
      const nameById = new Map(classStudents.map((s) => [s.id, s.fullName] as const));
      const letters: ParentLetter[] = [];
      for (const sid of ids) {
        const unused = (keys.get(sid) ?? []).find((k) => k.status === 'unused');
        if (unused) {
          letters.push({ studentName: nameById.get(sid) || 'your child', className: cls?.name || '', key: unused.key });
        }
      }
      letters.sort((a, b) => a.studentName.localeCompare(b.studentName));

      if (letters.length === 0) {
        setMsg({ ok: false, text: 'No letters to print — every child in this class has already registered.' });
        return;
      }

      const logo = await loadLogoDataUrl();
      const formUrl = `${window.location.origin}/parent-key.html`;
      const pdf = buildParentLetters(letters, {
        logoDataUrl: logo, formUrl, generatedBy: userData?.displayName || undefined,
      });
      pdf.save(`LKC_ParentLetters_${(cls?.name || 'Class').replace(/\s+/g, '_')}.pdf`);

      setMsg({
        ok: true,
        text: `Generated ${letters.length} letter${letters.length === 1 ? '' : 's'} for ${cls?.name}`
          + (created ? ` (${created} new key${created === 1 ? '' : 's'} created)` : '')
          + (alreadyDone ? `; ${alreadyDone} already registered — skipped` : '') + '.',
      });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setLetterBusy(false);
    }
  }

  // ---- Parent Registrations (review pending onboarding submissions) ----
  interface ChildSummary { studentId: string; name: string; className: string }
  interface OnboardingSubmission {
    id: string;
    // Phase 2 (batch, multi-child).
    studentIds?: string[];
    studentSummaries?: ChildSummary[];
    sourceKeys?: string[];
    parentPhone?: string;
    parentWhatsapp?: string;
    // Legacy single-child fields (submissions made before the batch upgrade).
    studentId?: string;
    studentName?: string;
    className?: string;
    parentFirstName: string;
    parentSurname: string;
    parentEmail: string;
    status: string;
    sourceKey?: string;
    submittedAt?: string;
  }
  const subChildren = (sub: OnboardingSubmission): ChildSummary[] => {
    if (sub.studentSummaries && sub.studentSummaries.length) return sub.studentSummaries;
    if (sub.studentId) return [{ studentId: sub.studentId, name: sub.studentName || '', className: sub.className || '' }];
    return [];
  };
  const [pending, setPending] = useState<OnboardingSubmission[]>([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approvedResult, setApprovedResult] = useState<
    { parentName: string; childCount: number; email: string; tempPassword?: string; isNewUser?: boolean } | null
  >(null);

  async function loadPending() {
    setPendingLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'reportParentOnboarding'), where('status', '==', 'pending')),
      );
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<OnboardingSubmission, 'id'>) }));
      rows.sort((a, b) => String(b.submittedAt ?? '').localeCompare(String(a.submittedAt ?? '')));
      setPending(rows);
    } catch (e) {
      setMsg({ ok: false, text: `Could not load registrations: ${(e as Error).message}` });
    } finally {
      setPendingLoading(false);
    }
  }
  useEffect(() => {
    void loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live count of pending self-service link requests (for the tab badge).
  const [linkReqCount, setLinkReqCount] = useState(0);
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, PARENT_LINK_REQUESTS), where('status', '==', 'pending')),
      (snap) => setLinkReqCount(snap.size),
      () => setLinkReqCount(0),
    );
    return unsub;
  }, []);

  // Write the parent's phone/whatsapp/email into the GUARDIAN CONTACT record on
  // a student (admissions.guardians[]) — NOT the auth account. Upsert by email.
  async function upsertGuardianContact(studentId: string, guardianId: string, sub: OnboardingSubmission) {
    const stu = students.find((s) => s.id === studentId);
    const guardians: Guardian[] = [...(stu?.admissions?.guardians ?? [])];
    const email = sub.parentEmail.toLowerCase();
    const fullName = `${sub.parentFirstName} ${sub.parentSurname}`.trim();
    const idx = guardians.findIndex((g) => (g.email || '').toLowerCase() === email);
    if (idx >= 0) {
      guardians[idx] = {
        ...guardians[idx],
        fullName: fullName || guardians[idx].fullName,
        email: sub.parentEmail,
        phone: sub.parentPhone || guardians[idx].phone,
        whatsapp: sub.parentWhatsapp || guardians[idx].whatsapp,
      };
    } else {
      guardians.push({
        id: guardianId,
        role: 'guardian',
        fullName,
        email: sub.parentEmail,
        phone: sub.parentPhone || undefined,
        whatsapp: sub.parentWhatsapp || undefined,
        isPrimaryContact: guardians.length === 0,
      });
    }
    /* KEEP parentContact IN STEP. It is the field the fee-reminder sweep
       used to read on its own, and three of the four paths that change a
       guardian's email never wrote it - which is how a parent could give the
       school a working address and still be chased at one the import
       invented from their child's surname. The sender no longer depends on
       it (lib/parentRecipient consults the portal account and the guardians
       first), but older screens still read it, so it must not go stale.

       Only fields the guardian actually has are written. Blanking
       parentContact.email because this guardian gave no address would
       destroy a good one to fix a stale one. */
    const primary = guardians.find((g) => g.isPrimaryContact) ?? guardians[0];
    const contactPatch: Record<string, string> = {};
    if (primary?.fullName) contactPatch['parentContact.guardianName'] = primary.fullName;
    if (primary?.email) contactPatch['parentContact.email'] = primary.email;
    if (primary?.whatsapp) contactPatch['parentContact.whatsapp'] = primary.whatsapp;

    await updateDoc(doc(db, COLLECTIONS.students, studentId), {
      'admissions.guardians': guardians,
      ...contactPatch,
      updatedAt: new Date().toISOString(),
      updatedBy: userData?.uid ?? '',
    });
  }

  async function approveSubmission(sub: OnboardingSubmission) {
    setActioningId(sub.id);
    setApprovedResult(null);
    try {
      const children = subChildren(sub);
      const studentIds = children.map((c) => c.studentId);
      if (studentIds.length === 0) throw new Error('This submission has no children to link.');
      const guardianName = `${sub.parentFirstName} ${sub.parentSurname}`.trim();
      // Reuse an existing guardian id on the first child if the email matches; else mint.
      const firstStu = students.find((s) => s.id === studentIds[0]);
      const existingGuardian = (firstStu?.admissions?.guardians ?? []).find(
        (g) => (g.email || '').toLowerCase() === sub.parentEmail.toLowerCase(),
      );
      const guardianId = existingGuardian?.id ?? crypto.randomUUID();

      // 1. Invitation record (createParentAccount flips it to 'accepted').
      const inviteRef = await addDoc(collection(db, COLLECTIONS.parentInvitations), {
        guardianId,
        guardianName,
        email: sub.parentEmail,
        studentIds,
        studentNames: children.map((c) => c.name),
        status: 'pending',
        invitedBy: userData?.uid ?? '',
        invitedByName: userData?.displayName ?? '',
        source: 'parentOnboarding',
        onboardingId: sub.id,
        createdAt: new Date().toISOString(),
      });

      // 2. Create ONE account linked to ALL children (createParentAccount merges studentIds).
      const createParent = httpsCallable<
        { invitationId: string; email: string; guardianName: string; guardianId: string; studentIds: string[] },
        { ok: boolean; uid: string; isNewUser: boolean; tempPassword?: string }
      >(functions, 'createParentAccount');
      const res = await createParent({ invitationId: inviteRef.id, email: sub.parentEmail, guardianName, guardianId, studentIds });

      // 3. Write parent phone/whatsapp to the guardian contact on EACH child
      //    (best-effort — a failed contact write never blocks the account).
      for (const sid of studentIds) {
        try { await upsertGuardianContact(sid, guardianId, sub); }
        catch (e) { console.warn('guardian contact write failed for', sid, e); }
      }

      // 4. Mark the submission done. Keys stay 'redeemed' (burned at redeem).
      await updateDoc(doc(db, 'reportParentOnboarding', sub.id), {
        status: 'account-created',
        accountUid: res.data?.uid ?? null,
        actionedBy: userData?.uid ?? '',
        actionedByName: userData?.displayName ?? '',
        actionedAt: new Date().toISOString(),
      });

      setPending((p) => p.filter((x) => x.id !== sub.id));
      setApprovedResult({
        parentName: guardianName,
        childCount: studentIds.length,
        email: sub.parentEmail,
        tempPassword: res.data?.tempPassword,
        isNewUser: res.data?.isNewUser,
      });
      setMsg({ ok: true, text: `Account created for ${guardianName} — linked to ${studentIds.length} child${studentIds.length === 1 ? '' : 'ren'}. Welcome email sent to ${sub.parentEmail}.` });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setActioningId(null);
    }
  }

  async function rejectSubmission(sub: OnboardingSubmission) {
    setActioningId(sub.id);
    try {
      await updateDoc(doc(db, 'reportParentOnboarding', sub.id), {
        status: 'rejected',
        rejectionReason: rejectReason.trim() || null,
        actionedBy: userData?.uid ?? '',
        actionedByName: userData?.displayName ?? '',
        actionedAt: new Date().toISOString(),
      });
      setPending((p) => p.filter((x) => x.id !== sub.id));
      setRejectingId(null);
      setRejectReason('');
      setMsg({ ok: true, text: `Registration from ${sub.parentFirstName} ${sub.parentSurname} rejected. Keys stay used — regenerate a new key if a child should re-register.` });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setActioningId(null);
    }
  }

  // ---- Send Registration Keys (reconciled email review + send) ----
  interface KeySendRow {
    id: string;
    studentName: string;
    className: string;
    sendEmail: string;
    sendTier: string;
    sendConfidence: string;
    sendStatus: string;
  }
  const [ksClassId, setKsClassId] = useState('');
  const [ksRows, setKsRows] = useState<KeySendRow[]>([]);
  const [ksLoading, setKsLoading] = useState(false);
  const [ksBusy, setKsBusy] = useState(false);

  async function loadKeySends(classId: string) {
    if (!classId) { setKsRows([]); return; }
    const cls = classes.find((c) => c.id === classId);
    setKsLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'reportParentKeySends'), where('className', '==', cls?.name || '')),
      );
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<KeySendRow, 'id'>) }));
      rows.sort((a, b) => String(a.studentName).localeCompare(String(b.studentName)));
      setKsRows(rows);
    } catch (e) {
      setMsg({ ok: false, text: `Could not load reconciliation: ${(e as Error).message}` });
    } finally {
      setKsLoading(false);
    }
  }

  const stampReview = () => ({ reviewedBy: userData?.uid ?? '', reviewedAt: new Date().toISOString() });
  async function ksSetStatus(id: string, status: string) {
    try {
      await updateDoc(doc(db, 'reportParentKeySends', id), { sendStatus: status, ...stampReview() });
      setKsRows((rows) => rows.map((r) => (r.id === id ? { ...r, sendStatus: status } : r)));
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  }
  async function ksSaveEmail(id: string, email: string) {
    const v = email.trim().toLowerCase();
    try {
      await updateDoc(doc(db, 'reportParentKeySends', id), { sendEmail: v, updatedAt: new Date().toISOString() });
      setKsRows((rows) => rows.map((r) => (r.id === id ? { ...r, sendEmail: v } : r)));
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  }
  async function ksReopen(id: string) {
    const ok = window.confirm(
      'This parent was already emailed. Reopen the row so you can correct the address and re-send the key?\n\n'
      + 'The existing key stays valid — the parent just receives a fresh email at the corrected address.',
    );
    if (!ok) return;
    try {
      await updateDoc(doc(db, 'reportParentKeySends', id), {
        sendStatus: 'pending-review',
        reopenedBy: userData?.uid ?? '',
        reopenedAt: new Date().toISOString(),
      });
      setKsRows((rows) => rows.map((r) => (r.id === id ? { ...r, sendStatus: 'pending-review' } : r)));
      setMsg({ ok: true, text: 'Row reopened — correct the email if needed, click Approve, then Send approved.' });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    }
  }
  async function ksApproveAllConfident() {
    const targets = ksRows.filter((r) => r.sendConfidence === 'confident' && !['emailed', 'excluded'].includes(r.sendStatus));
    for (const r of targets) await ksSetStatus(r.id, 'approved');
    setMsg({ ok: true, text: `Approved ${targets.length} confident row${targets.length === 1 ? '' : 's'}.` });
  }
  async function ksSend() {
    setKsBusy(true);
    try {
      const send = httpsCallable<
        { classId: string; formUrl: string },
        { ok: boolean; sent: unknown[]; failed: unknown[]; skipped: unknown[] }
      >(functions, 'sendParentKeys');
      const res = await send({ classId: ksClassId, formUrl: `${window.location.origin}/parent-key.html` });
      setMsg({
        ok: true,
        text: `Sent ${res.data.sent.length} key email${res.data.sent.length === 1 ? '' : 's'} · ${res.data.failed.length} failed · ${res.data.skipped.length} skipped. Failed/excluded students go to the letter batch.`,
      });
      await loadKeySends(ksClassId);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setKsBusy(false);
    }
  }
  // ---- Sync send-list from student records (dry-run → review → commit) ----
  interface SyncRow { studentId: string; studentName: string; className: string; email?: string; prevEmail?: string; status?: string }
  interface SyncPreview {
    counts: { add: number; update: number; skipInflight: number; skipNoEmail: number; nochange: number; scanned: number };
    add: SyncRow[]; update: SyncRow[]; skipInflight: SyncRow[]; skipNoEmail: SyncRow[];
  }
  const [syncClassId, setSyncClassId] = useState('');
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncCommitting, setSyncCommitting] = useState(false);
  const [syncPreview, setSyncPreview] = useState<SyncPreview | null>(null);
  const [syncExpand, setSyncExpand] = useState<string | null>(null);

  async function runSyncPreview() {
    setSyncBusy(true);
    setSyncPreview(null);
    setSyncExpand(null);
    try {
      const fn = httpsCallable<{ classId?: string }, SyncPreview & { ok: boolean }>(functions, 'previewSyncSendList');
      const { data } = await fn(syncClassId ? { classId: syncClassId } : {});
      setSyncPreview(data);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSyncBusy(false);
    }
  }
  async function runSyncCommit() {
    setSyncCommitting(true);
    try {
      const fn = httpsCallable<{ classId?: string }, { ok: boolean; added: number; updated: number; total: number }>(functions, 'commitSyncSendList');
      const { data } = await fn(syncClassId ? { classId: syncClassId } : {});
      setMsg({ ok: true, text: `Sync committed — added ${data.added}, updated ${data.updated}. New rows are pending-review, ready to review.` });
      setSyncPreview(null);
      if (ksClassId) await loadKeySends(ksClassId);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSyncCommitting(false);
    }
  }

  const ksApprovedCount = ksRows.filter((r) => r.sendStatus === 'approved').length;
  const KS_BADGE: Record<string, string> = {
    'pending-review': 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    emailed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    'email-failed': 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    excluded: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    'skipped-placeholder': 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  };

  // ---- Profile Updates (guardian-contact change requests) ----
  interface GuardianUpdateReq {
    id: string;
    parentName?: string;
    parentEmail?: string;
    studentName?: string;
    className?: string;
    proposedFields?: Record<string, string>;
    currentFields?: Record<string, string>;
    submittedAt?: string;
    status: string;
  }
  const [guRows, setGuRows] = useState<GuardianUpdateReq[]>([]);
  const [guLoading, setGuLoading] = useState(true);
  const [guActioningId, setGuActioningId] = useState<string | null>(null);
  const [guRejectId, setGuRejectId] = useState<string | null>(null);
  const [guRejectReason, setGuRejectReason] = useState('');

  async function loadGuardianUpdates() {
    setGuLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'reportGuardianUpdates'), where('status', '==', 'pending')));
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<GuardianUpdateReq, 'id'>) }));
      rows.sort((a, b) => String(b.submittedAt ?? '').localeCompare(String(a.submittedAt ?? '')));
      setGuRows(rows);
    } catch (e) {
      setMsg({ ok: false, text: `Could not load profile updates: ${(e as Error).message}` });
    } finally {
      setGuLoading(false);
    }
  }
  useEffect(() => {
    void loadGuardianUpdates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function approveGuardianUpdate(id: string) {
    setGuActioningId(id);
    try {
      await httpsCallable(functions, 'approveGuardianUpdate')({ requestId: id });
      setGuRows((r) => r.filter((x) => x.id !== id));
      setMsg({ ok: true, text: 'Update approved and applied to the record.' });
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
    finally { setGuActioningId(null); }
  }
  async function rejectGuardianUpdate(id: string) {
    setGuActioningId(id);
    try {
      await httpsCallable(functions, 'rejectGuardianUpdate')({ requestId: id, reason: guRejectReason.trim() });
      setGuRows((r) => r.filter((x) => x.id !== id));
      setGuRejectId(null);
      setGuRejectReason('');
      setMsg({ ok: true, text: 'Update rejected. Nothing was changed on the record.' });
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
    finally { setGuActioningId(null); }
  }

  // ---- Progress (read-only onboarding funnel dashboard) ----
  // Derives a per-student funnel from three existing collections. No writes.
  //   send-list universe : reportParentKeySends (one doc per student, id = studentId)
  //   redeemed / account : reportParentOnboarding submissions (joined on studentId)
  //   redeemed (fallback): parentAccessKeys docs with status 'redeemed'
  interface ProgRow {
    studentId: string;
    studentName: string;
    className: string;
    sendStatus: string;
    emailed: boolean;   // sendStatus === 'emailed'
    bounced: boolean;   // sendStatus === 'email-failed'
    approved: boolean;  // sendStatus === 'approved'
    redeemed: boolean;  // submission exists OR key redeemed OR account created
    account: boolean;   // onboarding submission reached 'account-created'
  }
  const [progRows, setProgRows] = useState<ProgRow[]>([]);
  const [progLoading, setProgLoading] = useState(false);
  const [progLoaded, setProgLoaded] = useState(false);
  const [progSort, setProgSort] = useState<{ key: 'className' | 'total' | 'emailed' | 'bounced' | 'redeemed' | 'account' | 'rate'; dir: 'asc' | 'desc' }>({ key: 'rate', dir: 'asc' });

  async function loadProgress() {
    setProgLoading(true);
    try {
      const [ksSnap, obSnap, keySnap] = await Promise.all([
        getDocs(collection(db, 'reportParentKeySends')),
        getDocs(collection(db, 'reportParentOnboarding')),
        getDocs(collection(db, 'parentAccessKeys')),
      ]);
      // studentId → redeemed / account, gathered from onboarding submissions.
      const redeemedIds = new Set<string>();
      const accountIds = new Set<string>();
      obSnap.forEach((d) => {
        const s = d.data() as OnboardingSubmission & { accountUid?: string };
        const ids = subChildren(s).map((c) => c.studentId).filter(Boolean);
        const isAccount = s.status === 'account-created' || !!s.accountUid;
        for (const id of ids) { redeemedIds.add(id); if (isAccount) accountIds.add(id); }
      });
      // A burned (redeemed) key is also proof the family responded.
      keySnap.forEach((d) => {
        const k = d.data() as { studentId?: string; status?: string };
        if (k.studentId && k.status === 'redeemed') redeemedIds.add(k.studentId);
      });
      const rows: ProgRow[] = ksSnap.docs.map((d) => {
        const r = d.data() as { studentId?: string; studentName?: string; className?: string; sendStatus?: string };
        const sid = r.studentId || d.id;
        const account = accountIds.has(sid);
        return {
          studentId: sid,
          studentName: r.studentName || '(unknown)',
          className: r.className || '(no class)',
          sendStatus: r.sendStatus || 'pending-review',
          emailed: r.sendStatus === 'emailed',
          bounced: r.sendStatus === 'email-failed',
          approved: r.sendStatus === 'approved',
          redeemed: account || redeemedIds.has(sid),
          account,
        };
      });
      rows.sort((a, b) => a.studentName.localeCompare(b.studentName));
      setProgRows(rows);
      setProgLoaded(true);
    } catch (e) {
      setMsg({ ok: false, text: `Could not load progress: ${(e as Error).message}` });
    } finally {
      setProgLoading(false);
    }
  }
  // Lazy-load the first time the Progress tab is opened.
  useEffect(() => {
    if (tab === 'progress' && !progLoaded && !progLoading) void loadProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const progSummary = useMemo(() => {
    const total = progRows.length;
    const emailed = progRows.filter((r) => r.emailed).length;
    const bounced = progRows.filter((r) => r.bounced).length;
    const redeemed = progRows.filter((r) => r.redeemed).length;
    const account = progRows.filter((r) => r.account).length;
    const rate = emailed > 0 ? Math.round((redeemed / emailed) * 100) : 0;
    return { total, emailed, bounced, redeemed, account, rate };
  }, [progRows]);

  const progByClass = useMemo(() => {
    const m = new Map<string, { className: string; total: number; emailed: number; bounced: number; redeemed: number; account: number }>();
    for (const r of progRows) {
      const c = m.get(r.className) ?? { className: r.className, total: 0, emailed: 0, bounced: 0, redeemed: 0, account: 0 };
      c.total += 1;
      if (r.emailed) c.emailed += 1;
      if (r.bounced) c.bounced += 1;
      if (r.redeemed) c.redeemed += 1;
      if (r.account) c.account += 1;
      m.set(r.className, c);
    }
    const rateOf = (c: { emailed: number; redeemed: number }) => (c.emailed > 0 ? c.redeemed / c.emailed : -1);
    const arr = [...m.values()];
    const { key, dir } = progSort;
    const mul = dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      if (key === 'className') return a.className.localeCompare(b.className) * mul;
      if (key === 'rate') return (rateOf(a) - rateOf(b)) * mul;
      return (a[key] - b[key]) * mul;
    });
    return arr;
  }, [progRows, progSort]);

  const progEmailedAwaiting = useMemo(() => progRows.filter((r) => r.emailed && !r.redeemed), [progRows]);
  const progBounced = useMemo(() => progRows.filter((r) => r.bounced), [progRows]);
  const progRedeemedAwaiting = useMemo(() => progRows.filter((r) => r.redeemed && !r.account), [progRows]);

  function progToggleSort(key: typeof progSort.key) {
    setProgSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'className' ? 'asc' : 'desc' }));
  }

  if (loading) return <Spinner label="Loading students…" />;

  const TABS: { id: SubTab; label: string; icon: typeof Mail }[] = [
    { id: 'keys', label: 'Registration Keys', icon: Mail },
    { id: 'requests', label: 'Link Requests', icon: UserPlus },
    { id: 'registrations', label: 'Parent Registrations', icon: Inbox },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'links', label: 'Child Links', icon: Link2 },
    { id: 'progress', label: 'Progress', icon: BarChart3 },
    { id: 'updates', label: 'Profile Updates', icon: UserCog },
    { id: 'letters', label: 'Letters', icon: FileText },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-[#1b3a6b] to-[#2d5295] text-white p-6">
        <div className="flex items-center gap-3">
          <div className="bg-white/10 rounded-xl p-2">
            <UserRound size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold">Parent Onboarding</h1>
            <p className="text-sm opacity-90">
              Email registration keys to families, review parent sign-ups, and
              print letters for families without an email.
            </p>
          </div>
        </div>
      </div>

      {msg && (
        <div
          className={`flex items-center gap-2 text-sm rounded-xl px-4 py-3 ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
              : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
          }`}
        >
          {msg.ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {msg.text}
        </div>
      )}

      {/* Sub-tab switcher (workflow order: Keys → Registrations → Letters) */}
      <div className="flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          const badge =
            t.id === 'registrations' && !pendingLoading ? pending.length
            : t.id === 'requests' ? linkReqCount
            : t.id === 'updates' && !guLoading ? guRows.length
            : 0;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`relative flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? 'seg-on'
                  : 'seg-off'
              }`}
            >
              <Icon size={16} />
              <span className="hidden sm:inline">{t.label}</span>
              {badge > 0 && (
                <span className="absolute top-1 right-1 sm:static sm:ml-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-[#2d5295] text-white text-[10px] font-semibold leading-none">
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ============ SUB-TAB 1: Registration Keys (email) ============ */}
      {tab === 'keys' && (
        <div className="space-y-6">

        {/* ---- Sync send-list from student records (dry-run → review → commit) ---- */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2 p-5 border-b border-gray-100 dark:border-gray-700">
            <UserCheck size={18} className="text-[#2d5295]" />
            <h2 className="font-semibold text-gray-900 dark:text-white">Sync send-list from student records</h2>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Teachers add guardian emails on the student record, but that doesn't automatically
              reach the send-list. This reconciles them: it <strong>previews</strong> what would change,
              and only writes when you <strong>commit</strong>. Students already emailed or further along
              are never touched; portal placeholders are never queued.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Scope</label>
                <select
                  value={syncClassId}
                  onChange={(e) => { setSyncClassId(e.target.value); setSyncPreview(null); }}
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                >
                  <option value="">Whole school</option>
                  {letterClasses.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>
              <button
                onClick={runSyncPreview}
                disabled={syncBusy || syncCommitting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#2d5295] text-[#2d5295] text-sm font-semibold hover:bg-[#2d5295]/5 disabled:opacity-40"
              >
                {syncBusy ? <Loader2 size={15} className="animate-spin" /> : <UserCheck size={15} />}
                Preview changes
              </button>
            </div>

            {syncPreview && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-4 bg-gray-50 dark:bg-gray-900/40 text-sm text-gray-700 dark:text-gray-200">
                  Scanned <strong>{syncPreview.counts.scanned}</strong> student{syncPreview.counts.scanned === 1 ? '' : 's'} —
                  {' '}will <strong className="text-emerald-600 dark:text-emerald-300">ADD {syncPreview.counts.add}</strong>,
                  {' '}<strong className="text-blue-600 dark:text-blue-300">UPDATE {syncPreview.counts.update}</strong>,
                  {' '}skip <strong className="text-amber-600 dark:text-amber-300">{syncPreview.counts.skipInflight}</strong> (already emailed / in flow),
                  {' '}skip <strong className="text-gray-500">{syncPreview.counts.skipNoEmail}</strong> (placeholder / no email),
                  {' '}<strong>{syncPreview.counts.nochange}</strong> unchanged.
                  {syncPreview.counts.add + syncPreview.counts.update === 0 && (
                    <span className="block mt-1 text-gray-500">Nothing to write — everything is already in sync.</span>
                  )}
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                  {([
                    { key: 'add', label: 'Add (new email → new send-row)', tone: 'text-emerald-700 dark:text-emerald-300', rows: syncPreview.add, showPrev: false },
                    { key: 'update', label: 'Update (corrected email, not yet sent)', tone: 'text-blue-700 dark:text-blue-300', rows: syncPreview.update, showPrev: true },
                    { key: 'skipInflight', label: 'Skipped — already emailed / in flow', tone: 'text-amber-700 dark:text-amber-300', rows: syncPreview.skipInflight, showPrev: false },
                    { key: 'skipNoEmail', label: 'Skipped — placeholder / no email', tone: 'text-gray-500', rows: syncPreview.skipNoEmail, showPrev: false },
                  ] as { key: string; label: string; tone: string; rows: SyncRow[]; showPrev: boolean }[]).map((b) => (
                    <div key={b.key}>
                      <button
                        onClick={() => setSyncExpand((k) => (k === b.key ? null : b.key))}
                        disabled={b.rows.length === 0}
                        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50 disabled:cursor-default"
                      >
                        <span className={`font-medium ${b.tone}`}>{b.label}</span>
                        <span className="flex items-center gap-2">
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{b.rows.length}</span>
                          {b.rows.length > 0 && <span className="text-gray-400 text-xs">{syncExpand === b.key ? '▲' : '▼'}</span>}
                        </span>
                      </button>
                      {syncExpand === b.key && b.rows.length > 0 && (
                        <div className="max-h-64 overflow-y-auto px-4 pb-3 space-y-1">
                          {b.rows.map((r) => (
                            <div key={r.studentId} className="flex items-center justify-between gap-2 text-xs">
                              <span className="text-gray-800 dark:text-gray-100 truncate">
                                {r.studentName} <span className="text-gray-400">· {r.className}</span>
                              </span>
                              <span className="text-gray-500 dark:text-gray-400 shrink-0 text-right">
                                {b.showPrev && r.prevEmail ? <span className="text-gray-400 line-through mr-1">{r.prevEmail}</span> : null}
                                {r.email || (r.status ? `(${r.status})` : '')}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100 dark:border-gray-700">
                  <button
                    onClick={() => { setSyncPreview(null); setSyncExpand(null); }}
                    disabled={syncCommitting}
                    className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 disabled:opacity-40"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={runSyncCommit}
                    disabled={syncCommitting || (syncPreview.counts.add + syncPreview.counts.update === 0)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {syncCommitting ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                    Commit changes ({syncPreview.counts.add + syncPreview.counts.update})
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ---- Send Registration Keys ---- */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2 p-5 border-b border-gray-100 dark:border-gray-700">
            <Mail size={18} className="text-[#2d5295]" />
            <h2 className="font-semibold text-gray-900 dark:text-white">
              Send Registration Keys
            </h2>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Review the reconciled parent email for each child, then email the
              key. <strong>Confident</strong> matches just need a glance;{' '}
              <strong>ambiguous</strong> rows (stream-move / undated) must be
              confirmed. Keys email only to the matched contact address — never a
              portal placeholder. Failed and excluded rows go to the Letters tab.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Class</label>
                <select
                  value={ksClassId}
                  onChange={(e) => { setKsClassId(e.target.value); void loadKeySends(e.target.value); }}
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                >
                  <option value="">Select a class…</option>
                  {letterClasses.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>
              {ksRows.length > 0 && (
                <>
                  <button
                    onClick={ksApproveAllConfident}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <Check size={14} /> Approve all confident
                  </button>
                  <button
                    onClick={ksSend}
                    disabled={ksBusy || ksApprovedCount === 0}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2d5295] text-white text-sm font-semibold hover:bg-[#1b3a6b] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {ksBusy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                    Send approved ({ksApprovedCount})
                  </button>
                </>
              )}
            </div>

            {ksLoading ? (
              <div className="text-sm text-gray-500 flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Loading…</div>
            ) : !ksClassId ? null : ksRows.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No reconciliation rows for this class. Run the key-sends migration first (staff/admin), then reload.
              </p>
            ) : (
              <div className="space-y-2">
                {ksRows.map((r) => {
                  const amb = r.sendConfidence !== 'confident';
                  const done = ['emailed', 'excluded', 'skipped-placeholder'].includes(r.sendStatus);
                  return (
                    <div key={r.id} className={`flex flex-col md:flex-row md:items-center gap-3 rounded-xl border p-3 ${amb ? 'border-amber-300 bg-amber-50/50 dark:border-amber-700 dark:bg-amber-900/10' : 'border-gray-200 dark:border-gray-700'}`}>
                      <div className="md:w-56 shrink-0">
                        <div className="font-medium text-gray-900 dark:text-white">{r.studentName}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {amb ? <span className="text-amber-700 dark:text-amber-300 font-medium">⚠ {r.sendConfidence} · {r.sendTier}</span> : <span>confident · {r.sendTier}</span>}
                        </div>
                      </div>
                      <input
                        type="email"
                        defaultValue={r.sendEmail}
                        onBlur={(e) => { if (e.target.value.trim().toLowerCase() !== r.sendEmail) ksSaveEmail(r.id, e.target.value); }}
                        disabled={done}
                        className="flex-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-900 dark:text-white disabled:opacity-60"
                      />
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${KS_BADGE[r.sendStatus] || 'bg-gray-100 text-gray-600'}`}>{r.sendStatus}</span>
                      {!done && (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            onClick={() => ksSetStatus(r.id, 'approved')}
                            disabled={r.sendStatus === 'approved'}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-40"
                          >
                            <Check size={13} /> Approve
                          </button>
                          <button
                            onClick={() => ksSetStatus(r.id, 'excluded')}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-xs hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <Ban size={13} /> Letter
                          </button>
                        </div>
                      )}
                      {r.sendStatus === 'emailed' && (
                        <button
                          onClick={() => ksReopen(r.id)}
                          title="Wrong email? Reopen this row to correct the address and re-send the key."
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-xs font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 shrink-0"
                        >
                          <RotateCcw size={13} /> Correct &amp; re-send
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-gray-400">
              Sending is admin-only; only <strong>approved</strong> rows are emailed. Each child gets one email (siblings share an address → one email each).
            </p>
          </div>
        </div>

        </div>
      )}

      {/* ============ SUB-TAB: Link Requests (self-service) ============ */}
      {tab === 'requests' && (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus size={18} className="text-[#2d5295]" />
            <h2 className="font-semibold text-gray-900 dark:text-white">Parent Link Requests</h2>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Requests parents submitted themselves from the public access page. Match each child to a pupil (check the date-of-birth agreement) and approve to create/link their account.
          </p>
          <ParentLinkRequests />
        </div>
      )}

      {/* ============ SUB-TAB 2: Parent Registrations ============ */}
      {tab === 'registrations' && (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between gap-2 p-5 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Inbox size={18} className="text-[#2d5295]" />
              <h2 className="font-semibold text-gray-900 dark:text-white">
                Parent Registrations
              </h2>
              {!pendingLoading && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#2d5295]/10 text-[#2d5295]">
                  {pending.length} pending
                </span>
              )}
            </div>
            <button
              onClick={() => void loadPending()}
              disabled={pendingLoading}
              className="text-xs text-[#2d5295] hover:underline disabled:opacity-40"
            >
              Refresh
            </button>
          </div>

          {approvedResult && (
            <div className="m-5 rounded-xl border border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20 p-4 text-sm">
              <div className="flex items-center gap-2 font-medium text-emerald-800 dark:text-emerald-200">
                <CheckCircle2 size={16} /> Account created for {approvedResult.parentName} — linked to {approvedResult.childCount} child{approvedResult.childCount === 1 ? '' : 'ren'}
              </div>
              <p className="mt-1 text-emerald-700 dark:text-emerald-300">
                A welcome email with sign-in details was sent to <strong>{approvedResult.email}</strong>.
              </p>
              {approvedResult.isNewUser === false ? (
                <p className="mt-1 text-emerald-700 dark:text-emerald-300">
                  This email already had a parent account — the child was linked to it (no new password).
                </p>
              ) : approvedResult.tempPassword ? (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-gray-600 dark:text-gray-300">Temporary password (fallback if the email doesn't arrive):</span>
                  <code className="px-2 py-0.5 rounded bg-white dark:bg-gray-800 border border-emerald-200 dark:border-emerald-700 font-mono">{approvedResult.tempPassword}</code>
                  <button
                    onClick={() => navigator.clipboard?.writeText(approvedResult.tempPassword!)}
                    className="inline-flex items-center gap-1 text-xs text-[#2d5295] hover:underline"
                  >
                    <Copy size={12} /> Copy
                  </button>
                </div>
              ) : null}
              <button onClick={() => setApprovedResult(null)} className="mt-2 text-xs text-gray-400 hover:underline">Dismiss</button>
            </div>
          )}

          <div className="p-5">
            {pendingLoading ? (
              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <Loader2 size={15} className="animate-spin" /> Loading registrations…
              </div>
            ) : pending.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No pending parent registrations. Submissions from the public registration form appear here for review.
              </p>
            ) : (
              <div className="space-y-2">
                {pending.map((sub) => {
                  const children = subChildren(sub);
                  return (
                  <div key={sub.id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                    <div className="flex flex-col md:flex-row md:items-start gap-3">
                      <div className="md:w-64 shrink-0">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {sub.parentFirstName} {sub.parentSurname}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {sub.parentEmail}
                          {sub.parentPhone ? ` · ${sub.parentPhone}` : ''}
                          {sub.submittedAt ? ` · ${new Date(sub.submittedAt).toLocaleDateString()}` : ''}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          Registering {children.length} child{children.length === 1 ? '' : 'ren'}:
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {children.map((c) => (
                            <span key={c.studentId} className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs bg-[#2d5295]/10 text-[#2d5295] dark:bg-[#2d5295]/25">
                              {c.name} · {c.className}
                            </span>
                          ))}
                        </div>
                      </div>
                      {rejectingId !== sub.id && (
                        <div className="flex items-center gap-2 md:ml-auto">
                          <button
                            onClick={() => approveSubmission(sub)}
                            disabled={actioningId === sub.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40"
                          >
                            {actioningId === sub.id ? <Loader2 size={14} className="animate-spin" /> : <UserCheck size={14} />}
                            Approve
                          </button>
                          <button
                            onClick={() => { setRejectingId(sub.id); setRejectReason(''); }}
                            disabled={actioningId === sub.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
                          >
                            <UserX size={14} /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                    {rejectingId === sub.id && (
                      <div className="mt-3 flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Reason (optional)"
                          className="flex-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-900 dark:text-white"
                        />
                        <button
                          onClick={() => rejectSubmission(sub)}
                          disabled={actioningId === sub.id}
                          className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40"
                        >
                          {actioningId === sub.id ? <Loader2 size={14} className="animate-spin" /> : <UserX size={14} />}
                          Confirm reject
                        </button>
                        <button
                          onClick={() => { setRejectingId(null); setRejectReason(''); }}
                          className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ SUB-TAB: Progress (read-only funnel dashboard) ============ */}
      {tab === 'progress' && (
        <div className="space-y-4">
          {/* Header + refresh */}
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between gap-2 p-5">
              <div className="flex items-center gap-2">
                <BarChart3 size={18} className="text-[#2d5295]" />
                <h2 className="font-semibold text-gray-900 dark:text-white">Onboarding Progress</h2>
                <span className="text-xs text-gray-400">read-only</span>
              </div>
              <button
                onClick={() => void loadProgress()}
                disabled={progLoading}
                className="inline-flex items-center gap-1.5 text-xs text-[#2d5295] hover:underline disabled:opacity-40"
              >
                {progLoading ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} className="rotate-180" />}
                Refresh
              </button>
            </div>
          </div>

          {progLoading && progRows.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2 px-1">
              <Loader2 size={15} className="animate-spin" /> Loading progress…
            </div>
          ) : progLoaded && progRows.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 px-1">
              No send-list rows found. Run the key-sends migration first, then refresh.
            </p>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { label: 'In send-list', value: progSummary.total, icon: Mail, tone: 'text-gray-700 dark:text-gray-200' },
                  { label: 'Emailed', value: progSummary.emailed, icon: MailCheck, tone: 'text-blue-600 dark:text-blue-300' },
                  { label: 'Bounced', value: progSummary.bounced, icon: MailX, tone: 'text-red-600 dark:text-red-300' },
                  { label: 'Redeemed', value: progSummary.redeemed, icon: KeyRound, tone: 'text-emerald-600 dark:text-emerald-300' },
                  { label: 'Account created', value: progSummary.account, icon: UserPlus, tone: 'text-indigo-600 dark:text-indigo-300' },
                  { label: 'Response rate', value: `${progSummary.rate}%`, icon: BarChart3, tone: 'text-[#2d5295] dark:text-blue-200', hint: 'redeemed ÷ emailed' },
                ].map((c) => {
                  const Icon = c.icon;
                  return (
                    <div key={c.label} className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                        <Icon size={13} /> {c.label}
                      </div>
                      <div className={`mt-1 text-2xl font-bold ${c.tone}`}>{c.value}</div>
                      {c.hint && <div className="text-[10px] text-gray-400 mt-0.5">{c.hint}</div>}
                    </div>
                  );
                })}
              </div>

              {/* Per-class breakdown (sortable) */}
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                <div className="p-4 border-b border-gray-100 dark:border-gray-700 text-sm font-semibold text-gray-900 dark:text-white">
                  By class <span className="font-normal text-gray-400">— click a column to sort (default: lowest response rate first)</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                        {([
                          ['className', 'Class'], ['total', 'Sent'], ['emailed', 'Emailed'],
                          ['bounced', 'Bounced'], ['redeemed', 'Redeemed'], ['account', 'Accounts'], ['rate', 'Response'],
                        ] as [typeof progSort.key, string][]).map(([key, label], i) => (
                          <th
                            key={key}
                            onClick={() => progToggleSort(key)}
                            className={`px-4 py-2.5 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 ${i === 0 ? '' : 'text-right'}`}
                          >
                            {label}
                            {progSort.key === key && <span className="ml-1">{progSort.dir === 'asc' ? '▲' : '▼'}</span>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {progByClass.map((c) => {
                        const rate = c.emailed > 0 ? Math.round((c.redeemed / c.emailed) * 100) : null;
                        return (
                          <tr key={c.className} className="border-b border-gray-50 dark:border-gray-700/50 last:border-0">
                            <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white">{c.className}</td>
                            <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-300">{c.total}</td>
                            <td className="px-4 py-2.5 text-right text-blue-600 dark:text-blue-300">{c.emailed}</td>
                            <td className={`px-4 py-2.5 text-right ${c.bounced ? 'text-red-600 dark:text-red-300 font-medium' : 'text-gray-400'}`}>{c.bounced}</td>
                            <td className="px-4 py-2.5 text-right text-emerald-600 dark:text-emerald-300">{c.redeemed}</td>
                            <td className="px-4 py-2.5 text-right text-indigo-600 dark:text-indigo-300">{c.account}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-gray-900 dark:text-white">{rate === null ? '—' : `${rate}%`}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Drill-down lists */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {([
                  { title: 'Emailed, awaiting response', hint: 'the chase list', rows: progEmailedAwaiting, icon: MailCheck, tone: 'text-blue-600 dark:text-blue-300' },
                  { title: 'Bounced', hint: 'need a printed letter', rows: progBounced, icon: MailX, tone: 'text-red-600 dark:text-red-300' },
                  { title: 'Redeemed, awaiting approval', hint: 'staff action needed', rows: progRedeemedAwaiting, icon: UserPlus, tone: 'text-emerald-600 dark:text-emerald-300' },
                ]).map((list) => {
                  const Icon = list.icon;
                  return (
                    <div key={list.title} className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col">
                      <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                        <div className={`flex items-center gap-1.5 text-sm font-semibold ${list.tone}`}>
                          <Icon size={15} /> {list.title}
                          <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{list.rows.length}</span>
                        </div>
                        <div className="text-[11px] text-gray-400 mt-0.5">{list.hint}</div>
                      </div>
                      <div className="p-2 max-h-80 overflow-y-auto">
                        {list.rows.length === 0 ? (
                          <p className="text-xs text-gray-400 p-3">None — nothing outstanding here.</p>
                        ) : (
                          list.rows.map((r) => (
                            <div key={r.studentId} className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              <span className="text-sm text-gray-800 dark:text-gray-100 truncate">{r.studentName}</span>
                              <span className="text-xs text-gray-400 shrink-0">{r.className}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ============ SUB-TAB: Profile Updates (guardian-contact review) ============ */}
      {tab === 'updates' && (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between gap-2 p-5 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <UserCog size={18} className="text-[#2d5295]" />
              <h2 className="font-semibold text-gray-900 dark:text-white">Profile Updates</h2>
              {!guLoading && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#2d5295]/10 text-[#2d5295]">
                  {guRows.length} pending
                </span>
              )}
            </div>
            <button onClick={() => void loadGuardianUpdates()} disabled={guLoading} className="text-xs text-[#2d5295] hover:underline disabled:opacity-40">Refresh</button>
          </div>
          <div className="p-5">
            {guLoading ? (
              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Loading…</div>
            ) : guRows.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No pending profile updates. Contact changes submitted by parents in the portal appear here for review.</p>
            ) : (
              <div className="space-y-2">
                {guRows.map((req) => (
                  <div key={req.id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                    <div className="flex flex-col md:flex-row md:items-start gap-3">
                      <div className="md:w-56 shrink-0">
                        <div className="font-medium text-gray-900 dark:text-white">{req.parentName || 'Parent'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{req.parentEmail}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          for <span className="font-medium">{req.studentName}</span>{req.className ? ` · ${req.className}` : ''}
                        </div>
                      </div>
                      <div className="flex-1 space-y-1">
                        {Object.keys(req.proposedFields ?? {}).map((k) => (
                          <div key={k} className="flex items-center gap-2 text-sm flex-wrap">
                            <span className="w-20 text-gray-500 dark:text-gray-400">{GU_LABEL[k] ?? k}</span>
                            <span className="text-gray-400 line-through">{req.currentFields?.[k] || '(blank)'}</span>
                            <span className="text-gray-400">→</span>
                            <span className="font-medium text-[#2d5295]">{req.proposedFields?.[k]}</span>
                          </div>
                        ))}
                      </div>
                      {guRejectId !== req.id && (
                        <div className="flex items-center gap-2 md:ml-auto">
                          <button
                            onClick={() => approveGuardianUpdate(req.id)}
                            disabled={guActioningId === req.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40"
                          >
                            {guActioningId === req.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                            Approve
                          </button>
                          <button
                            onClick={() => { setGuRejectId(req.id); setGuRejectReason(''); }}
                            disabled={guActioningId === req.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
                          >
                            <Ban size={14} /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                    {guRejectId === req.id && (
                      <div className="mt-3 flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          value={guRejectReason}
                          onChange={(e) => setGuRejectReason(e.target.value)}
                          placeholder="Reason (optional)"
                          className="flex-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-900 dark:text-white"
                        />
                        <button onClick={() => rejectGuardianUpdate(req.id)} disabled={guActioningId === req.id} className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40">
                          {guActioningId === req.id ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                          Confirm reject
                        </button>
                        <button onClick={() => { setGuRejectId(null); setGuRejectReason(''); }} className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300">Cancel</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ SUB-TAB: Messages (onboarding queries) ============ */}
      {tab === 'messages' && (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare size={18} className="text-[#2d5295]" />
            <h2 className="font-semibold text-gray-900 dark:text-white">Onboarding messages</h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Parent queries about registration / portal access — including those raised when a parent's
            "add a child" code is rejected. Open a thread to read and reply.
          </p>
          <SchoolCommunications restrictCategory="onboarding" embedded />
        </div>
      )}

      {/* ============ SUB-TAB: Child Links (who's linked to whom) ============ */}
      {tab === 'links' && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Link2 size={18} className="text-[#2d5295]" />
            <h2 className="font-semibold text-gray-900 dark:text-white">Child links</h2>
            <span className="text-xs text-gray-400">which children are linked to which parent account</span>
          </div>
          <ChildLinks />
        </div>
      )}

      {/* ============ SUB-TAB 3: Letters (fallback) ============ */}
      {tab === 'letters' && (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2 p-5 border-b border-gray-100 dark:border-gray-700">
            <FileText size={18} className="text-[#2d5295]" />
            <h2 className="font-semibold text-gray-900 dark:text-white">
              Parent Portal Letters
            </h2>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Fallback for families without an email. Generate a printable letter
              for each child in a class — carrying their unique registration key
              and instructions — to print and send home. Children who have already
              registered are skipped.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Class
                </label>
                <select
                  value={letterClassId}
                  onChange={(e) => setLetterClassId(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                >
                  <option value="">Select a class…</option>
                  {letterClasses.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={generateLetters}
                disabled={letterBusy || !letterClassId}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#2d5295] text-white text-sm font-semibold hover:bg-[#1b3a6b] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {letterBusy ? <Loader2 size={15} className="animate-spin" /> : <Printer size={15} />}
                Generate parent letters
              </button>
            </div>
            <p className="text-xs text-gray-400">
              One combined PDF, one page per child. The letter shows the child's
              name + key and the registration link. Generation is admin-only.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
